import { Typography } from '@trellis/roots/typography'
import { AlertCircle, Ban, Pencil, XCircle } from 'lucide-react'

import { formatCalendarDate } from '@/lib/format'

export interface InvoiceStatusBannerProps {
  status: string
  dueAt: string | null
}

export function InvoiceStatusBanner({ status, dueAt }: InvoiceStatusBannerProps) {
  if (status === 'OVERDUE') {
    return (
      <div className="mb-4 flex items-start gap-3.5 rounded-xl border border-rose-100 bg-rose-50 p-4">
        <AlertCircle className="mt-0.5 size-[18px] shrink-0 text-rose-600" />
        <div>
          <p className="text-sm font-semibold text-rose-900">This invoice is overdue.</p>
          <Typography
            variant="body-sm"
            className="mt-0.5 leading-relaxed text-rose-900/85"
          >
            Payment was due {dueAt ? formatCalendarDate(dueAt) : '—'}. Consider sending a
            reminder or following up directly.
          </Typography>
        </div>
      </div>
    )
  }
  if (status === 'DRAFT') {
    return (
      <div className="mb-4 flex items-start gap-3.5 rounded-xl border border-sky-100 bg-sky-50 p-4">
        <Pencil className="mt-0.5 size-[18px] shrink-0 text-sky-600" />
        <div>
          <p className="text-sm font-semibold text-sky-900">This invoice is a draft.</p>
          <Typography
            variant="body-sm"
            className="mt-0.5 leading-relaxed text-sky-900/85"
          >
            It hasn't been sent yet. Review the line items and send when ready.
          </Typography>
        </div>
      </div>
    )
  }
  if (status === 'CANCELLED') {
    return (
      <div className="bg-muted mb-4 flex items-start gap-3.5 rounded-xl border p-4">
        <XCircle className="text-muted-foreground mt-0.5 size-[18px] shrink-0" />
        <div>
          <Typography variant="body" color="muted" className="font-semibold">
            This invoice was cancelled.
          </Typography>
          <Typography
            variant="body-sm"
            className="text-muted-foreground/85 mt-0.5 leading-relaxed"
          >
            No payment is expected. The line items are kept for reference.
          </Typography>
        </div>
      </div>
    )
  }
  if (status === 'VOID') {
    return (
      <div className="bg-muted mb-4 flex items-start gap-3.5 rounded-xl border p-4">
        <Ban className="text-muted-foreground mt-0.5 size-[18px] shrink-0" />
        <div>
          <Typography variant="body" color="muted" className="font-semibold">
            This invoice was voided.
          </Typography>
          <Typography
            variant="body-sm"
            className="text-muted-foreground/85 mt-0.5 leading-relaxed"
          >
            It's kept in the system for audit purposes but is not collectible.
          </Typography>
        </div>
      </div>
    )
  }
  return null
}
